// Admin · Curated Drops.
// Operator maintains the public /drops page: an ordered list of Shopify
// product handles. Products also still appear on /collections/<type>.
// Auth: shared operator password — same token key as community/hero-drop.

import { useCallback, useEffect, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "loadout.admin.community.token";

function useToken() {
	const [token, setToken] = useState(() => {
		try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
	});
	const save = useCallback((t) => {
		setToken(t);
		try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
	}, []);
	const clear = useCallback(() => {
		setToken("");
		try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
	}, []);
	return [token, save, clear];
}

function LoginPanel({ onLogin }) {
	const [pw, setPw] = useState("");
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState("");
	const submit = async (e) => {
		e.preventDefault();
		setErr(""); setBusy(true);
		try {
			const r = await fetch(`${BACKEND}/api/admin/community/login`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ password: pw }),
			});
			if (!r.ok) { setErr(r.status === 401 ? "Wrong password." : `Login failed (${r.status}).`); return; }
			const j = await r.json();
			onLogin(j.token);
		} catch { setErr("Network error. Try again."); } finally { setBusy(false); }
	};
	return (
		<main data-testid="admin-drops-login-page" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
			<form onSubmit={submit} className="w-full max-w-[420px] border border-white/15 bg-black">
				<div className="border-b border-white/10 px-6 py-6">
					<p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
					<h1 className="mt-2 font-display text-[26px] uppercase">Drops</h1>
					<p className="mt-3 font-sans text-[13px] text-white/60">
						Curate the /drops page. Same operator password as other admin pages.
					</p>
				</div>
				<div className="px-6 py-6 space-y-4">
					<input
						data-testid="admin-drops-login-password"
						type="password" value={pw} onChange={(e) => setPw(e.target.value)}
						placeholder="Operator password" autoFocus
						className="w-full bg-transparent border border-white/20 px-3 py-3 font-mono text-[13px] placeholder:text-white/30 focus:outline-none focus:border-[#FF9F1C]"
					/>
					{err && <p data-testid="admin-drops-login-error" className="font-mono text-[11px] text-red-400">{err}</p>}
					<button
						data-testid="admin-drops-login-submit"
						type="submit" disabled={busy}
						className="w-full border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase py-3.5 disabled:opacity-50"
					>
						{busy ? "Signing in…" : "Sign in"}
					</button>
				</div>
			</form>
		</main>
	);
}

function AddHandleForm({ token, onAdded }) {
	const [handle, setHandle] = useState("");
	const [busy, setBusy] = useState(false);
	const [err, setErr] = useState("");

	const submit = async (e) => {
		e.preventDefault();
		const h = handle.trim().toLowerCase();
		if (!h) { setErr("Handle required."); return; }
		setErr(""); setBusy(true);
		try {
			const r = await fetch(`${BACKEND}/api/admin/drops`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ handle: h }),
			});
			const j = await r.json().catch(() => ({}));
			if (!r.ok) {
				if (r.status === 404) setErr(`No product with handle '${h}'`);
				else setErr(j.detail || `Add failed (${r.status}).`);
				return;
			}
			setHandle("");
			onAdded();
		} catch { setErr("Network error."); } finally { setBusy(false); }
	};

	return (
		<form onSubmit={submit} className="border border-white/15 bg-black p-6 space-y-4" data-testid="admin-drops-add-form">
			<div>
				<label className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60">
					Add product by Shopify handle
				</label>
				<div className="mt-2 flex gap-3">
					<input
						data-testid="admin-drops-handle"
						value={handle} onChange={(e) => setHandle(e.target.value)}
						placeholder="aula-hero-68-hero68-he-series"
						className="flex-1 bg-transparent border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white placeholder:text-white/25 focus:outline-none focus:border-[#FF9F1C]"
					/>
					<button
						data-testid="admin-drops-add-submit"
						type="submit" disabled={busy}
						className="border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[12px] tracking-[0.2em] uppercase px-6 py-3 disabled:opacity-50"
					>
						{busy ? "Adding…" : "Add"}
					</button>
				</div>
			</div>
			{err && (
				<p data-testid="admin-drops-add-error" className="font-mono text-[11px] text-red-400">
					{err}
				</p>
			)}
		</form>
	);
}

function DropRow({ row, token, onChanged, isFirst, isLast }) {
	const [busy, setBusy] = useState(false);
	const p = row.product;

	const move = async (direction) => {
		setBusy(true);
		try {
			const r = await fetch(`${BACKEND}/api/admin/drops/${row.id}/move`, {
				method: "POST",
				headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
				body: JSON.stringify({ direction }),
			});
			if (r.ok) onChanged();
		} finally { setBusy(false); }
	};
	const remove = async () => {
		if (!window.confirm(`Remove ${row.handle} from Drops?`)) return;
		setBusy(true);
		try {
			const r = await fetch(`${BACKEND}/api/admin/drops/${row.id}`, {
				method: "DELETE",
				headers: { Authorization: `Bearer ${token}` },
			});
			if (r.ok) onChanged();
		} finally { setBusy(false); }
	};

	return (
		<div data-testid="admin-drops-row" className="border border-white/10 bg-black p-4 flex items-center gap-4">
			<div className="w-16 h-16 bg-[#111] overflow-hidden shrink-0">
				{p?.image ? (
					<img src={p.image} alt={p.title || row.handle} className="w-full h-full object-cover" loading="lazy" />
				) : (
					<div className="w-full h-full flex items-center justify-center font-mono text-[9px] text-white/40 uppercase">no image</div>
				)}
			</div>
			<div className="flex-1 min-w-0">
				<p className="font-display text-[16px] text-white uppercase leading-tight truncate">
					{p?.title || row.handle}
				</p>
				<p className="mt-1 font-mono text-[11px] text-white/50 truncate">
					/{row.handle}
					{p?.price && <span className="ml-3 text-white/70">₹{Number(p.price).toLocaleString("en-IN")}</span>}
				</p>
				{!p && (
					<p className="mt-1 font-mono text-[10px] text-red-400 uppercase tracking-[0.14em]">
						Product no longer resolves
					</p>
				)}
			</div>
			<div className="flex items-center gap-1">
				<button
					data-testid="admin-drops-move-up"
					type="button" onClick={() => move("up")} disabled={busy || isFirst}
					className="w-9 h-9 border border-white/20 text-white font-mono disabled:opacity-30"
					aria-label="Move up"
				>↑</button>
				<button
					data-testid="admin-drops-move-down"
					type="button" onClick={() => move("down")} disabled={busy || isLast}
					className="w-9 h-9 border border-white/20 text-white font-mono disabled:opacity-30"
					aria-label="Move down"
				>↓</button>
				<button
					data-testid="admin-drops-remove"
					type="button" onClick={remove} disabled={busy}
					className="ml-2 w-9 h-9 border border-red-400/60 text-red-400 font-mono disabled:opacity-40"
					aria-label="Remove"
				>×</button>
			</div>
		</div>
	);
}

function AdminPanel({ token, onLogout }) {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState("");

	const reload = useCallback(async () => {
		setLoading(true); setErr("");
		try {
			const r = await fetch(`${BACKEND}/api/admin/drops/list`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (r.status === 401) { onLogout(); return; }
			const j = await r.json();
			setRows(j.drops || []);
		} catch { setErr("Could not load drops list."); } finally { setLoading(false); }
	}, [token, onLogout]);

	useEffect(() => { reload(); }, [reload]);

	return (
		<main data-testid="admin-drops-panel" className="min-h-screen bg-[#0A0A0A] text-white">
			<div className="mx-auto max-w-[960px] px-6 py-10">
				<div className="flex items-center justify-between border-b border-white/10 pb-6">
					<div>
						<p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
						<h1 className="mt-2 font-display text-[32px] uppercase">Drops</h1>
						<p className="mt-2 font-sans text-[13px] text-white/60">
							Curated overlay list for /drops. Products still appear on their normal collection pages.
						</p>
					</div>
					<button
						data-testid="admin-drops-logout"
						onClick={onLogout}
						className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/60 border border-white/20 px-4 py-2 hover:text-white hover:border-white"
					>Sign out</button>
				</div>

				<section className="mt-8 space-y-4">
					<AddHandleForm token={token} onAdded={reload} />
				</section>

				<section className="mt-8 space-y-3" data-testid="admin-drops-list">
					{loading && <p className="font-mono text-[12px] text-white/40 uppercase tracking-[0.14em]">Loading…</p>}
					{err && <p className="font-mono text-[12px] text-red-400">{err}</p>}
					{!loading && rows.length === 0 && (
						<p data-testid="admin-drops-empty" className="font-mono text-[12px] text-white/40 uppercase tracking-[0.14em]">
							No curated drops yet. Add a product above.
						</p>
					)}
					{rows.map((row, i) => (
						<DropRow
							key={row.id}
							row={row}
							token={token}
							onChanged={reload}
							isFirst={i === 0}
							isLast={i === rows.length - 1}
						/>
					))}
				</section>
			</div>
		</main>
	);
}

export default function AdminDrops() {
	const [token, saveToken, clearToken] = useToken();
	if (!token) return <LoginPanel onLogin={saveToken} />;
	return <AdminPanel token={token} onLogout={clearToken} />;
}
